// src/services/adminCommunaute.js
// Domaine Communauté — façade Worker Admin → Worker Communauté uniquement.
// Aucun appel direct URL_WORKER_COMMUNAUTE · aucun HMAC côté navigateur.

import { apiFetch, buildQuery } from './adminHttp';

/* ---------- Vue d’ensemble / statistiques — COMMUNAUTE_VOIR ---------- */

/**
 * GET /admin/communaute/overview
 * Query optionnelle (période) selon contrat : ex. period, from, to — passer uniquement ce qui est fourni.
 */
export async function getCommunauteOverview(params = {}) {
  return apiFetch(`/admin/communaute/overview${buildQuery(params)}`, {
    method: 'GET',
  });
}

/** GET /admin/communaute/contacts/stats */
export async function getContactsStats(params = {}) {
  return apiFetch(`/admin/communaute/contacts/stats${buildQuery(params)}`, {
    method: 'GET',
  });
}

/** GET /admin/communaute/conversations/stats */
export async function getConversationsStats(params = {}) {
  return apiFetch(`/admin/communaute/conversations/stats${buildQuery(params)}`, {
    method: 'GET',
  });
}

/* ---------- Membres — COMMUNAUTE_VOIR — pagination limit + cursor ---------- */

/** GET /admin/communaute/members */
export async function getCommunauteMembers(params = {}) {
  return apiFetch(`/admin/communaute/members${buildQuery(params)}`, {
    method: 'GET',
  });
}

/** GET /admin/communaute/members/:id */
export async function getCommunauteMember(id) {
  return apiFetch(`/admin/communaute/members/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

/** GET /admin/communaute/members/:id/contacts */
export async function getMemberContacts(id, params = {}) {
  return apiFetch(
    `/admin/communaute/members/${encodeURIComponent(id)}/contacts${buildQuery(params)}`,
    { method: 'GET' }
  );
}

/* ---------- Espaces — COMMUNAUTE_VOIR ---------- */

/** GET /admin/communaute/spaces */
export async function getSpaces(params = {}) {
  return apiFetch(`/admin/communaute/spaces${buildQuery(params)}`, {
    method: 'GET',
  });
}

/** GET /admin/communaute/spaces/:id */
export async function getSpace(id) {
  return apiFetch(`/admin/communaute/spaces/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

/* ---------- Activité — COMMUNAUTE_VOIR ---------- */

/** GET /admin/communaute/activity */
export async function getActivity(params = {}) {
  return apiFetch(`/admin/communaute/activity${buildQuery(params)}`, {
    method: 'GET',
  });
}

/* ---------- Appels — COMMUNAUTE_VOIR ---------- */

/** GET /admin/communaute/calls */
export async function getCalls(params = {}) {
  return apiFetch(`/admin/communaute/calls${buildQuery(params)}`, {
    method: 'GET',
  });
}

/** GET /admin/communaute/calls/:id */
export async function getCall(id) {
  return apiFetch(`/admin/communaute/calls/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

/* ---------- Notifications (observation, pas de payload complet) — COMMUNAUTE_VOIR ---------- */

/** GET /admin/communaute/notifications */
export async function getCommunauteNotifications(params = {}) {
  return apiFetch(`/admin/communaute/notifications${buildQuery(params)}`, {
    method: 'GET',
  });
}

/* ---------- Support — COMMUNAUTE_SUPPORT ---------- */

/**
 * POST /admin/communaute/support/messages
 * Body : { memberId, body }
 */
export async function sendSupportMessage(payload) {
  return apiFetch('/admin/communaute/support/messages', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * GET /admin/communaute/support/messages/pending
 * Pagination : limit + after=<messageId> (≠ cursor des autres listes)
 */
export async function getSupportPending(params = {}) {
  return apiFetch(
    `/admin/communaute/support/messages/pending${buildQuery(params)}`,
    { method: 'GET' }
  );
}

/**
 * POST /admin/communaute/support/messages/ack
 * Body : { messageId } OU { messageIds: [...] }
 */
export async function ackSupportMessages(payload) {
  return apiFetch('/admin/communaute/support/messages/ack', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * GET /admin/communaute/support/conversations
 * Liste des fils Support (un par membre). Params : limit, before=<lastMessageAt en ms>
 */
export async function getSupportConversations(params = {}) {
  return apiFetch(`/admin/communaute/support/conversations${buildQuery(params)}`, {
    method: 'GET',
  });
}

/**
 * GET /admin/communaute/support/conversations/:memberId/messages
 * Historique d'un fil. Params : limit, before=<createdAt en ms du plus ancien message déjà reçu>
 */
export async function getSupportConversationMessages(memberId, params = {}) {
  return apiFetch(
    `/admin/communaute/support/conversations/${encodeURIComponent(memberId)}/messages${buildQuery(params)}`,
    { method: 'GET' }
  );
}

/* ---------- Notify — COMMUNAUTE_NOTIFY ---------- */

/**
 * POST /admin/communaute/notify
 * Body exact : { memberId, type }
 */
export async function notifyMember(payload) {
  return apiFetch('/admin/communaute/notify', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/* ---------- Média — COMMUNAUTE_MEDIA_VOIR / COMMUNAUTE_MEDIA_MODIFIER ---------- */

/** GET /admin/communaute/media/providers */
export async function getMediaProviders(params = {}) {
  return apiFetch(`/admin/communaute/media/providers${buildQuery(params)}`, {
    method: 'GET',
  });
}

/**
 * POST /admin/communaute/media/providers
 * Body selon contrat (ex. providerKey, …)
 */
export async function createMediaProvider(payload) {
  return apiFetch('/admin/communaute/media/providers', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * POST /admin/communaute/media/providers/:id/credentials
 * Transmet le body au Worker Admin. Ne jamais journaliser ni afficher les secrets en clair.
 */
export async function setMediaProviderCredentials(id, payload) {
  return apiFetch(
    `/admin/communaute/media/providers/${encodeURIComponent(id)}/credentials`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
}

/** GET /admin/communaute/media/usage */
export async function getMediaUsage(params = {}) {
  return apiFetch(`/admin/communaute/media/usage${buildQuery(params)}`, {
    method: 'GET',
  });
}

/* ---------- Origines — lecture seule — COMMUNAUTE_VOIR ---------- */

/**
 * GET /admin/communaute/origins
 * Aucune mutation locale : les changements passent par POST/PATCH /admin/origins (global).
 */
export async function getCommunauteOrigins() {
  return apiFetch('/admin/communaute/origins', {
    method: 'GET',
  });
}
